import { useState, useCallback } from 'react';
import { post, get } from '../../api';

type Status = 'idle' | 'loading' | 'success' | 'error';

interface UseWizardSubmitReturn<T> {
  status: Status;
  error: string | null;
  data: T | null;
  /** POST request */
  submit: (path: string, body?: unknown) => Promise<T | null>;
  /** GET request */
  fetch: (path: string) => Promise<T | null>;
  /** Reset to idle */
  reset: () => void;
}

export default function useWizardSubmit<T = unknown>(): UseWizardSubmitReturn<T> {
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<T | null>(null);

  const reset = useCallback(() => {
    setStatus('idle');
    setError(null);
    setData(null);
  }, []);

  const submit = useCallback(async (path: string, body?: unknown): Promise<T | null> => {
    setStatus('loading');
    setError(null);
    try {
      const result = await post<T>(path, body);
      setData(result);
      setStatus('success');
      // Notify dashboard (and other listeners) that data changed
      window.dispatchEvent(new CustomEvent('plate:data-changed'));
      return result;
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message || 'Ein Fehler ist aufgetreten';
      setError(msg);
      setStatus('error');
      return null;
    }
  }, []);

  const fetchData = useCallback(async (path: string): Promise<T | null> => {
    setStatus('loading');
    setError(null);
    try {
      const result = await get<T>(path);
      setData(result);
      setStatus('success');
      return result;
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message || 'Ein Fehler ist aufgetreten';
      setError(msg);
      setStatus('error');
      return null;
    }
  }, []);

  return { status, error, data, submit, fetch: fetchData, reset };
}
